/**
 * Phone MONITORING (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile Mine.dc.html:
 * 262-343`). AA phase 4.
 *
 * The sticky bar (`◂ MY GAMES` · MONITORING · the stat), a full-width search,
 * then one CARD per watched game: a 44px header row (cover · name · source · the
 * bell), a seven-cell labelled lamp strip, and — only while PRICE is armed —
 * a 44px `PRICE FLOOR · LIST $X · −40% OFF` row with `− $N +`. WHAT FIRED is
 * a panel below the list. No column headers, so no column toggle here.
 */

import { useEffect } from 'react';

import { Bell } from '../components/Bell.tsx';
import { CoverArt } from '../components/CoverArt.tsx';
import { PageBar } from '../components/PageBar.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { monitoringVM } from '../store/selectors.ts';
import type { MonRow } from '../store/selectors.ts';
import { TR } from '../lib/watch.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Card({ r }: { r: MonRow }): JSX.Element {
  const st = useQd();
  const art = useArt(r.id + 'p', r.coverCands);
  const priceOn = r.cells.some((c) => c.isPrice && c.on);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--gt-line)', background: 'var(--gt-tile)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0 6px 10px', minHeight: 44 }}>
        <div onClick={() => st.openGame(r.id)} style={{ position: 'relative', width: 26, height: 39, flex: 'none', background: r.fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={7} ini={r.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span onClick={() => st.openGame(r.id)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{r.n}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ fontFamily: MONO, fontSize: 7, letterSpacing: '.08em', color: r.srcClr, border: '1px ' + r.srcBs + ' ' + r.srcClr, padding: '1px 4px', flex: 'none' }}>{r.srcL}</span>
            <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.05em', color: 'var(--gt-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</span>
          </div>
        </div>
        <div onClick={() => st.wOpen(r.id)} data-tip={r.bellTip} style={{ width: 44, height: 44, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, borderLeft: '1px solid var(--gt-line)', color: r.bellOn ? 'var(--acc)' : 'var(--gt-text-4)', cursor: 'pointer' }}>
          <Bell on={r.bellOn} size={12} color={r.bellOn ? 'var(--acc)' : 'var(--gt-text-4)'} />
          {r.bellOn ? <span style={{ fontFamily: MONO, fontSize: 9 }}>{r.bellN}</span> : null}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: '1px solid var(--gt-line)' }}>
        {r.cells.map((c, i) => (
          <div
            key={c.key}
            onClick={() => (c.na ? undefined : void st.monTog(r.id, c.k))}
            style={{ height: 52, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, background: c.on ? 'var(--accSoft)' : 'transparent', borderLeft: i ? '1px solid var(--gt-line-0)' : 'none', cursor: c.na ? 'not-allowed' : 'pointer' }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                boxSizing: 'border-box',
                border: '1px ' + (c.na ? 'dashed' : 'solid') + ' ' + (c.on ? 'var(--acc)' : c.na ? 'var(--gt-border-2)' : 'var(--gt-tick)'),
                background: c.on ? 'var(--acc)' : 'transparent',
                display: 'grid',
                placeItems: 'center',
                fontFamily: MONO,
                fontSize: 8,
                color: c.on ? 'var(--gt-ink)' : c.na ? 'var(--gt-ghost)' : 'var(--gt-text-5)',
              }}
            >
              {c.on ? '■' : c.na ? '–' : c.isPrice ? '$' : ''}
            </span>
            <span style={{ fontFamily: MONO, fontSize: 6, letterSpacing: '.06em', color: c.on ? 'var(--acc)' : c.na ? 'var(--gt-ghost)' : 'var(--gt-faint)', whiteSpace: 'nowrap' }}>{TR[i]!.l}</span>
          </div>
        ))}
      </div>
      {priceOn ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px', height: 44, borderTop: '1px solid var(--gt-line)' }}>
          <span style={{ flex: 1, minWidth: 0, fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: 'var(--gt-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.capRowTxt}</span>
          <div style={{ display: 'flex', border: '1px solid var(--acc)' }}>
            <span onClick={() => void st.monCap(r.id, -1)} style={{ width: 34, height: 30, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 12, color: 'var(--acc)', cursor: 'pointer' }}>−</span>
            <span style={{ minWidth: 38, height: 30, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 10, color: 'var(--acc)', borderLeft: '1px solid var(--acc)', borderRight: '1px solid var(--acc)' }}>${r.cap}</span>
            <span onClick={() => void st.monCap(r.id, 1)} style={{ width: 34, height: 30, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 12, color: 'var(--acc)', cursor: 'pointer' }}>+</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function Monitoring(): JSX.Element {
  const s = useQd();
  const v = monitoringVM(s);
  const monEnter = useQd((st) => st.monEnter);
  useEffect(() => monEnter(), [monEnter]);

  // A watched game need be on no other surface — `TELL ME WHEN` is reachable
  // from Detail, and MONITORING is where an off-list watch lives — so this
  // screen fetches its own art (the note in `components/useArt.ts`).
  useQueueArt(v.rows.map((r) => r.id));

  return (
    <div data-screen-label="Monitoring" style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <PageBar title="MONITORING" sub={v.monStat} />
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox value={v.monQ} onChange={(e) => s.setMonQ(e.target.value)} onClear={() => s.setMonQ('')} placeholder="SEARCH MONITORED GAMES…" w="100%" ifs={12} pad="13px 12px" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px 0' }}>
        {v.rows.map((r) => (
          <Card key={r.key} r={r} />
        ))}
        {v.empty ? (
          <div style={{ padding: '30px 0', textAlign: 'center', fontFamily: MONO, fontSize: 10, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>{v.emptyTxt}</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '22px 14px 26px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>WHAT FIRED</span>
          <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>{v.firedSub}</span>
        </div>
        <div style={{ borderTop: '1px solid var(--gt-line)' }}>
          {v.fired.map((f) => (
            <div key={f.key} onClick={() => s.openGame(f.id)} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 0', borderBottom: '1px solid var(--gt-line-0)', cursor: 'pointer', minHeight: 44 }}>
              <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.09em', color: f.tagClr, background: f.tagBg, border: '1px solid ' + f.tagClr, padding: '3px 5px', flex: 'none', marginTop: 1 }}>{f.tag}</span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.n}</span>
                <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 13, lineHeight: 1.45, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{f.t}</span>
              </div>
              <span style={{ fontFamily: MONO, fontSize: 8.5, color: 'var(--gt-tick)', flex: 'none' }}>{f.w}</span>
            </div>
          ))}
          {v.firedEmpty ? (
            <div style={{ padding: '22px 0', fontFamily: MONO, fontSize: 9.5, letterSpacing: '.07em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>{v.firedEmptyTxt}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
